/**
 * ScamBlocker AI — Markalı Numaralar (/branded)
 * Kurumun doğrulanmış arama numaraları: ekle (phone,label) / sil.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Hash, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

interface Branded {
  id: string;
  phone_masked: string;
  label?: string;
  verified_at?: string;
}

export function BrandedNumbersPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ phone: '', label: '' });

  const { data, isLoading, isError } = useQuery<{ numbers: Branded[] }>({
    queryKey: ['branded'],
    queryFn: async () => (await apiClient.get(API.org.branded)).data,
  });

  const addMut = useMutation({
    mutationFn: async () => (await apiClient.post(API.org.branded, form)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['branded'] });
      setForm({ phone: '', label: '' });
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      window.alert(e.response?.data?.error ?? 'Numara eklenemedi'),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(API.org.brandedById(id))).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['branded'] }),
  });

  const list = data?.numbers ?? [];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Hash size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>Markalı Numaralar</h1>
        {data && <span className={styles.countPill}>{list.length}</span>}
      </div>

      <div className={styles.formRow}>
        <input
          className={styles.input}
          placeholder="Telefon (05XX...)"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <input
          className={styles.input}
          placeholder="Etiket (ör. Çağrı Merkezi)"
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
        />
        <button
          type="button"
          className={styles.actionBtn}
          disabled={!form.phone || addMut.isPending}
          onClick={() => addMut.mutate()}
        >
          <Plus size={14} /> Ekle
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>Numaralar yüklenemedi.</div>}
        {!isLoading && !isError && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Numara</th>
                <th>Etiket</th>
                <th>Doğrulama</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((n) => (
                <tr key={n.id} className={styles.row}>
                  <td className={styles.mono}>{n.phone_masked}</td>
                  <td>{n.label ?? '—'}</td>
                  <td>
                    {n.verified_at ? (
                      <span className={styles.badge} style={{ color: 'var(--color-success)' }}>
                        <CheckCircle2 size={12} /> Doğrulandı
                      </span>
                    ) : (
                      <span className={styles.badge} style={{ color: 'var(--color-muted)' }}>
                        Beklemede
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => window.confirm('Numara silinsin mi?') && delMut.mutate(n.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={4} className={styles.empty}>
                    Numara yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
